<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.configSections.zoneInformation") }}</h5>
              <div class="d-flex gap-1">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="addNewZone"
                  :disabled="isLoadingZones || isSavingZone"
                  class="text-capitalize"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.zoneInformation.addZone") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingZones" class="text-center py-4">
              <b-spinner
                variant="primary"
                :label="$t('dmp.zoneInformation.loadingZones')"
              ></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.zoneInformation.loadingZones") }}</p>
            </div>

            <b-row v-if="!isLoadingZones">
              <b-col cols="4" class="table-column">
                <!-- Zones Table -->
                <b-table
                  :items="zones"
                  :fields="zoneTableFields"
                  responsive
                  striped
                  hover
                  show-empty
                  :empty-text="$t('dmp.zoneInformation.noZonesConfigured')"
                  selectable
                  select-mode="single"
                  @row-selected="onRowSelected"
                  sort-by="number"
                  :sort-desc="false"
                  class="mb-0"
                >
                  <template #cell(number)="data">
                    {{ data.item.number }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.name }}
                  </template>

                  <template #cell(type)="data">
                    {{ data.item.type }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="8" class="details-column">
                <!-- Selected Zone Details Card -->
                <b-card v-if="selectedZone" no-body>
                  <b-card-header class="mb-0card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.zoneInformation.zoneDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="zoneFormObserver">
                      <b-form @submit.prevent="saveZone">
                        <!-- Tabs for General and 734 Options -->
                        <b-tabs v-model="activeTab" pills class="zone-details-tabs">
                          <b-tab :title="$t('dmp.zoneInformation.standard')" active>
                            <zone-standard
                              :zone-form="zoneForm"
                              :is-editing="isEditingZone"
                              :is-saving="isSavingZone"
                            />
                          </b-tab>
                          <b-tab :title="$t('dmp.zoneInformation.action')">
                            <zone-action
                              :zone-form="zoneForm"
                              :is-saving="isSavingZone"
                            />
                          </b-tab>
                          <b-tab :title="$t('dmp.zoneInformation.wirelessVPlex')">
                            <zone-wireless
                              :zone-form="zoneForm"
                              :is-saving="isSavingZone"
                            />
                          </b-tab>
                          <b-tab :title="$t('dmp.zoneInformation.advanced')">
                            <zone-advanced
                              :zone-form="zoneForm"
                              :is-saving="isSavingZone"
                            />
                          </b-tab>
                        </b-tabs>

                        <!-- Action Buttons -->
                        <b-row class="mt-3">
                          <b-col cols="12" class="d-flex justify-content-end">
                            <b-button
                              v-if="isEditingZone"
                              variant="outline-danger"
                              size="sm"
                              class="mr-1"
                              @click="deleteZone"
                              :disabled="isSavingZone || isDeletingZone"
                            >
                              <b-spinner v-if="isDeletingZone" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="TrashIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button
                              variant="primary"
                              size="sm"
                              type="submit"
                              :disabled="isSavingZone || isDeletingZone"
                            >
                              <b-spinner v-if="isSavingZone" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="SaveIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{
                                isSavingZone ? $t("actions.saving") : $t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, watch, onMounted, getCurrentInstance } from "@vue/composition-api";
import { ValidationObserver } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import ZoneStandard from "./ZoneStandard.vue";
import ZoneAction from "./ZoneAction.vue";
import ZoneWireless from "./ZoneWireless.vue";
import ZoneAdvanced from "./ZoneAdvanced.vue";
import {
  mapZoneFromBackend,
  mapZoneToBackend,
  mapZoneToTemplate,
  getDefaultZoneValues,
  formatZoneNumber,
} from "./useZoneConfig";
import { useTemplateMode } from "../useTemplateMode";

export default {
  name: "ConfigZoneInformation",
  components: {
    ValidationObserver,
    ToastificationContent,
    ZoneStandard,
    ZoneAction,
    ZoneWireless,
    ZoneAdvanced,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
      default: null,
    },
    zoneInformationData: {
      type: Array,
      required: false,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    const isLoadingZones = ref(props.loading);
    const isSavingZone = ref(false);
    const isDeletingZone = ref(false);
    const zones = ref([]);
    const selectedZone = ref(null);
    const activeTab = ref(0);

    const isEditingZone = ref(false);
    const zoneFormObserver = ref(null);

    const zoneForm = ref(getDefaultZoneValues());

    const buildValidationToastText = (observer) => {
      const fallbackText = proxy.$t("dmp.common.pleaseFixErrors");
      const errorBag = observer?.errors && typeof observer.errors === "object"
        ? observer.errors
        : {};

      const messages = Object.values(errorBag)
        .flat()
        .filter((m) => typeof m === "string" && m.trim().length > 0);

      if (!messages.length) return fallbackText;

      const uniqueMessages = [...new Set(messages)];

      const escapeHtml = (value) => value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

      return uniqueMessages.map(escapeHtml).join("<br>");
    };

    const extractErrorMessage = (error, fallbackKey) => {
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          return error.response.data.message;
        }
        if (error.response.statusText) {
          return `Error ${error.response.status}: ${error.response.statusText}`;
        }
        return `Error ${error.response.status}`;
      }
      if (error.message && !error.message.includes('Cannot read properties')) {
        return error.message;
      }
      return proxy.$t(fallbackKey);
    };

    const zoneTableFields = [
      { key: "zoneNumber", label: t("dmp.zoneInformation.number") },
      { key: "zoneName", label: t("dmp.zoneInformation.name") },
      { key: "zoneType", label: t("dmp.zoneInformation.type") },
    ];

    const loadZones = () => {
      try {
        const backendZones = Array.isArray(props.zoneInformationData)
          ? props.zoneInformationData
          : [];

        zones.value = backendZones.map((zone) => {
          // Map backend data to table format
          const mappedZone = mapZoneFromBackend(zone);
          return {
            zoneNumber: parseInt(zone.zoneNumber),
            zoneName: zone.zoneName,
            zoneType: zone.zoneType,
            _raw: mappedZone, // Save mapped data for editing
          };
        });
      } catch (error) {
        console.error("Error loading zones:", error);
        zones.value = [];
      }
    };

    const addNewZone = () => {
      if (zones.value.length >= 999) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("messages.warning.title"),
            text: proxy.$t("dmp.zoneInformation.maxZonesReached"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Calculate next available zone number
      const existingNumbers = zones.value
        .map((z) => parseInt(z.zoneNumber, 10))
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);

      let nextZoneNumber = 1;
      for (const num of existingNumbers) {
        if (num === nextZoneNumber) {
          nextZoneNumber++;
        } else {
          break;
        }
      }

      isEditingZone.value = false;
      activeTab.value = 0;

      // Create temporary zone with default values
      selectedZone.value = {
        zoneNumber: parseInt(nextZoneNumber),
        zoneName: `ZONE ${nextZoneNumber}`,
        zoneType: "--",
      };

      // Reset form with default values
      zoneForm.value = {
        ...getDefaultZoneValues(),
        zoneNumber: parseInt(nextZoneNumber),
        zoneName: `ZONE ${nextZoneNumber}`,
      };
    };

    const deleteZone = async () => {
      if (!isEditingZone.value || !selectedZone.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.zoneInformationData || []).filter(
          (z) =>
            parseInt(z.zoneNumber, 10) !==
            parseInt(selectedZone.value.zoneNumber, 10)
        );
        emitTemplateUpdate("zone_information", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.zoneInformation.messages.zoneDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedZone.value = null;
        isEditingZone.value = false;
        zoneForm.value = getDefaultZoneValues();
        return;
      }

      isDeletingZone.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const zoneToDelete = selectedZone.value;

        const response = await store.dispatch("dmp_panels/deleteZone", {
          uuid: props.uuid,
          zoneNumber: zoneToDelete.zoneNumber,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.zoneInformation.messages.zoneDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedZone.value = null;
          isEditingZone.value = false;
          zoneForm.value = getDefaultZoneValues();
        } else {
          throw new Error(response.message || "Failed to delete zone");
        }
      } catch (error) {
        console.error("Error deleting zone:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: extractErrorMessage(error, "dmp.zoneInformation.messages.zoneDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeletingZone.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    const saveZone = async () => {
      // Validate form
      const isValid = await zoneFormObserver.value?.validate();
      if (!isValid) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("messages.warning.title"),
            text: buildValidationToastText(zoneFormObserver.value),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
          timeout: 5000,
        });
        return;
      }

      if (isTemplateMode.value) {
        const list = [...(props.zoneInformationData || [])];
        const idx = list.findIndex(
          (z) =>
            parseInt(z.zoneNumber, 10) ===
            parseInt(zoneForm.value.zoneNumber, 10)
        );
        const original = idx !== -1 ? list[idx] : {};
        const item = mapZoneToTemplate(zoneForm.value, original);
        if (idx !== -1) {
          item.id = original.id != null ? original.id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        emitTemplateUpdate("zone_information", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: isEditingZone.value
              ? proxy.$t("dmp.zoneInformation.messages.zoneUpdateSuccess")
              : proxy.$t("dmp.zoneInformation.messages.zoneCreateSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedZone.value = null;
        isEditingZone.value = false;
        zoneForm.value = getDefaultZoneValues();
        return;
      }

      isSavingZone.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        // Convert form data to backend format
        const zoneToSave = mapZoneToBackend(zoneForm.value);

        const response = await store.dispatch("dmp_panels/setZone", {
          uuid: props.uuid,
          zoneData: zoneToSave,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: isEditingZone.value
                ? proxy.$t("dmp.zoneInformation.messages.zoneUpdateSuccess")
                : proxy.$t("dmp.zoneInformation.messages.zoneCreateSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          selectedZone.value = null;
          isEditingZone.value = false;
          zoneForm.value = getDefaultZoneValues();
        } else {
          throw new Error(response.message || "Failed to save zone");
        }
      } catch (error) {
        console.error("Error saving zone:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: extractErrorMessage(error, "dmp.zoneInformation.messages.zoneSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSavingZone.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        isEditingZone.value = true;
        selectedZone.value = selected;
        activeTab.value = 0;

        // Load zone data into form (already mapped)
        if (selected._raw) {
          zoneForm.value = { ...selected._raw };
        } else {
          zoneForm.value = getDefaultZoneValues();
        }
      } else {
        selectedZone.value = null;
        isEditingZone.value = false;
        zoneForm.value = getDefaultZoneValues();
      }
    };

    onMounted(() => {
      loadZones();
    });

    watch(
      () => props.uuid,
      (newUuid) => {
        if (newUuid) {
          loadZones();
        }
      }
    );

    watch(
      () => props.zoneInformationData,
      (newData) => {
        if (newData) {
          loadZones();
        }
      },
      { deep: true }
    );

    return {
      zones,
      isLoadingZones,
      isSavingZone,
      isDeletingZone,
      zoneForm,
      zoneFormObserver,
      isEditingZone,
      zoneTableFields,
      selectedZone,
      activeTab,

      addNewZone,
      deleteZone,
      saveZone,
      onRowSelected,

      refFormObserver,
      getValidationState,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.details-column {
  .card {
    position: sticky;
    top: 5rem;
  }
}

.zone-details-tabs {
  ::v-deep .nav-pills {
    .nav-link {
      padding: 0.5rem 1rem;
    }
  }
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
